import * as THREE from 'three';
import GraphNode from 'src/utils/graph/graph_node';
import {
	createCylinder,
	createLine,
	createSphere,
	extrudeShape,
	makeExtrusionMaterial,
	makeShape,
	parseSVG,
} from 'src/pages/plan/utils/objects';
import { makeOutline, simplifyPath } from 'src/utils/shape_processor/path';
import planConfig from 'src/config/plan';

export function makeGraph(nodes, links) {
	const companyMap = {};

	const graph: Record<string, GraphNode<number>> = nodes.reduce((acc, node) => {
		const id = parseInt(node.id.split('-')[1], 10);
		acc[node.id] = new GraphNode(id, null, { x: node.x, y: node.y, z: node.z }, node.type);

		if (node.company_id) {
			companyMap[node.company_id] = id;
		}

		return acc;
	}, {});

	links.forEach((link) => {
		const n1 = graph[link.first_node_id];
		const n2 = graph[link.second_node_id];

		if (n1 && n2) {
			n1.link(n2, link.weight, false, null, link.distance);

			n1.linkTypes[n2.id] = link.type;
		}
	});

	return [graph, companyMap];
}

export function drawGraph(graph, context) {
	const nodes: any[] = Object.values(graph);

	nodes.forEach((node) => {
		createNode(node, context);
	});

	const visitedLinks = {};
	nodes.forEach((node) => {
		node.links.forEach((link) => {
			const k1 = `${node.rawId}-${link.rawId}`;
			const k2 = `${link.rawId}-${node.rawId}`;

			if (!visitedLinks[k1] && !visitedLinks[k2]) {
				visitedLinks[k1] = true;
				visitedLinks[k2] = true;

				createLink(node, link, node.linkTypes[link.id], context);
			}
		});
	});
}

export function createNode(node, context) {
	const mesh = createSphere(2, { x: node.coords.x, y: 5, z: node.coords.y }, 0x999999);

	context.scene.add(mesh);

	return mesh;
}

export function createLink(firstNode, secondNode, type, context) {
	const mesh = createLine(
		{ x: firstNode.coords.x, y: 5, z: firstNode.coords.y },
		{ x: secondNode.coords.x, y: 5, z: secondNode.coords.y },
		type === 'virtual' ? 0x333333 : 0x666666
	);

	context.scene.add(mesh);

	return mesh;
}

export function createPath(nodes: any[], pathsConfig: any) {
	const paths: any[] = [];
	let path: any[] = [];

	for (let i = 0; i < nodes.length - 1; i++) {
		const n1 = nodes[i];
		const n2 = nodes[i + 1];

		if (n1.linkTypes[n2.id] === 'virtual') {
			path.push(n1);
			paths.push(path);
			path = [n2];
		} else {
			path.push(n1);
		}
	}
	path.push(nodes.at(-1));
	paths.push(path);

	const pathsGroup = new THREE.Group();

	paths.forEach((path: any[]) => {
		const points = path.map((n) => ({
			x: Math.floor(n.coords.x * 100) / 100,
			y: Math.floor(n.coords.y * 100) / 100,
			z: Math.floor(n.coords.z * 100) / 100,
		}));

		const simplifiedPoints = simplifyPath(points);

		const lineShape = makeOutline(simplifiedPoints, pathsConfig.width, false);

		const [shape, pos]: any[] = makeShape(lineShape);

		const geometry = extrudeShape(shape, pathsConfig);

		const material = makeExtrusionMaterial(pathsConfig);

		const mesh = new THREE.Mesh(geometry, material);
		mesh.position.set(pos.x, pathsConfig.depth, pos.y);

		pathsGroup.add(mesh);
	});

	const simplifiedPath = simplifyPath(nodes.map((node) => node.coords)).map(
		(coords) => new THREE.Vector3(coords.x, pathsConfig.depth, coords.y)
	);
	const pathCurve = new THREE.CatmullRomCurve3(simplifiedPath, false, 'catmullrom', 0);

	let pathLength = 0;
	for (let i = 0; i < nodes.length - 1; i++) {
		const n1 = nodes[i];
		const n2 = nodes[i + 1];

		pathLength += Math.hypot(n1.coords.x - n2.coords.x, n1.coords.y - n2.coords.y);
	}

	const fn = nodes[0];
	const ln = nodes.at(-1);
	const distance = Math.hypot(ln.coords.x - fn.coords.x, fn.coords.y - ln.coords.y);

	// const angle = Math.atan2(fn.coords.y - ln.coords.y, fn.coords.x - ln.coords.x);
	// const nx = ln.coords.x + Math.cos(angle) * (distance + planConfig.navigation.cameraStartOffset);
	// const nz = ln.coords.y + Math.sin(angle) * (distance + planConfig.navigation.cameraStartOffset);
	//
	// const lx = ln.coords.x + Math.cos(angle) * planConfig.navigation.cameraEndOffset;
	// const lz = ln.coords.y + Math.sin(angle) * planConfig.navigation.cameraEndOffset;
	//
	// const cameraCurve = new THREE.CatmullRomCurve3(
	// 	[
	// 		new Vector3(nx, planConfig.navigation.cameraStartOffset, nz),
	// 		new Vector3(lx, planConfig.navigation.cameraEndOffset, lz),
	// 	],
	// 	false,
	// 	'catmullrom',
	// 	0
	// );

	const simplifiedCameraPath = simplifyPath(nodes.map((node) => node.coords)).map(
		(coords) =>
			new THREE.Vector3(
				coords.x + planConfig.navigation.cameraXOffset,
				planConfig.navigation.cameraZOffset,
				coords.y + planConfig.navigation.cameraYOffset
			)
	);
	const cameraCurve = new THREE.CatmullRomCurve3(simplifiedCameraPath, false, 'catmullrom', 0);

	return [pathsGroup, simplifiedPath, pathCurve, cameraCurve, distance, pathLength];
}

export function makeSource(source) {
	const sourceConfig: any = planConfig.objects.source;

	const group = new THREE.Group();

	const mesh1 = createCylinder(
		sourceConfig.outer.radius,
		sourceConfig.height,
		sourceConfig.segments,
		{ x: source.coords.x, y: 0.1, z: source.coords.y },
		sourceConfig.outer.faceColor
	);

	const mesh2 = createCylinder(
		sourceConfig.inner.radius,
		sourceConfig.height,
		sourceConfig.segments,
		{ x: source.coords.x, y: 1, z: source.coords.y },
		sourceConfig.inner.faceColor
	);

	group.add(mesh1);
	group.add(mesh2);

	return group;
}

export function makeMarker() {
	const markerConfig = planConfig.objects.marker;

	const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" id="navigation">
	<path d="M12.93 4.26l6.15 14.99c.34.83-.51 1.66-1.33 1.29l-5.34-2.36c-.26-.11-.55-.11-.81 0l-5.34 2.36c-.82.36-1.67-.46-1.33-1.29l6.15-14.99c.33-.83 1.51-.83 1.85 0z"></path>
</svg>`;
	const paths = parseSVG(svg);

	const group = new THREE.Group();
	const shape = paths[0].shapes[0];

	const g1 = extrudeShape(shape, markerConfig.inner);
	g1.scale(markerConfig.inner.scale, markerConfig.inner.scale, markerConfig.inner.scale);
	g1.translate(
		-12 + (1 - markerConfig.inner.scale) * 12,
		markerConfig.inner.depth + markerConfig.yOffset + markerConfig.inner.yOffset,
		-12 + (1 - markerConfig.inner.scale) * 12
	);
	g1.rotateY(Math.PI);

	const g2 = extrudeShape(shape, markerConfig.outer);
	g2.translate(-12, markerConfig.outer.depth + markerConfig.yOffset, -12);
	g2.rotateY(Math.PI);

	const mat1 = makeExtrusionMaterial(markerConfig.inner);
	const mat2 = makeExtrusionMaterial(markerConfig.outer);

	const mesh1 = new THREE.Mesh(g1, mat1);
	const mesh2 = new THREE.Mesh(g2, mat2);

	group.add(mesh1);
	group.add(mesh2);

	group.scale.set(markerConfig.scale, markerConfig.scale, markerConfig.scale);

	return group;
}
